import React, { useCallback, useEffect, useRef, useState } from "react";

const deviceSizes = {
  desktop: { width: 1440, height: 900 },
  tablet: { width: 820, height: 1180 },
  mobile: { width: 390, height: 844 },
};

export default function LiveActivityMap({ report, mode, overlay, websiteBase: savedWebsiteBase = "", device = report.device || "desktop" }) {
  const [minimumClicks, setMinimumClicks] = useState(1);
  const [section, setSection] = useState("all");
  const [opacity, setOpacity] = useState(80);
  const [spotSize, setSpotSize] = useState(100);
  const frameRef = useRef(null);
  const frameOriginRef = useRef(null);
  const previewRef = useRef(null);
  const viewportRef = useRef(null);
  const touchYRef = useRef(null);
  const scrollbarDragRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const { width: deviceWidth, height: deviceHeight } = deviceSizes[device] || deviceSizes.desktop;
  const scale = availableWidth > 0 ? Math.min(1, availableWidth / deviceWidth) : 1;
  const [viewport, setViewport] = useState(null);
  const [frameOrigin, setFrameOrigin] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);

  useEffect(() => {
    const container = previewRef.current;
    if (!container) return undefined;
    const measure = () => setAvailableWidth(container.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(entries => setAvailableWidth(entries[0].contentRect.width));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const visiblePoints = report.points.filter((point) => {
    const inSection =
      section === "all" ||
      (section === "top" && point.y < 33) ||
      (section === "middle" && point.y >= 33 && point.y < 67) ||
      (section === "bottom" && point.y >= 67);
    return point.count >= minimumClicks && inSection;
  });
  const resetFilters = () => {
    setMinimumClicks(1);
    setSection("all");
    setOpacity(80);
    setSpotSize(100);
  };
  let websiteUrl = "";
  const websiteBase = savedWebsiteBase.trim();
  try {
    if (report.page) {
      const page = String(report.page).trim();
      // Absolute tracked URLs do not depend on the configured website base.
      const url = /^https?:\/\//i.test(page)
        ? new URL(page)
        : new URL(page, websiteBase || undefined);
      if (["http:", "https:"].includes(url.protocol)) {
        url.searchParams.set("cloudkia_preview", "1");
        websiteUrl = url.href;
      }
    }
  } catch {
    // Relative tracked paths need the public website URL configured.
  }

  useEffect(() => {
    setViewport(null);
    frameOriginRef.current = null;
    setFrameOrigin(null);
    if (!websiteUrl) return undefined;
    const receiveViewport = (event) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      let eventProtocol;
      try {
        eventProtocol = new URL(event.origin).protocol;
      } catch {
        return;
      }
      if (!/^https?:$/.test(eventProtocol)) return;
      if (frameOriginRef.current && event.origin !== frameOriginRef.current) return;
      const data = event.data;
      if (data?.type !== "cloudkia:preview-viewport" || data.version !== 1) return;
      const { width, height, scrollX, scrollY, path } = data;
      if (![width, height, scrollX, scrollY].every(Number.isFinite) ||
          width <= 0 || height <= 0 || width > 1000000 || height > 1000000 ||
          scrollX < 0 || scrollY < 0 || typeof path !== "string") return;
      frameOriginRef.current = event.origin;
      setFrameOrigin(event.origin);
      setViewport({ width, height, scrollX, scrollY, path });
    };
    const requestViewport = () => {
      // The bootstrap message carries no sensitive data. A wildcard lets the
      // widget receive it after a cross-origin redirect; replies are validated
      // against the iframe window and pin the actual origin below.
      frameRef.current?.contentWindow?.postMessage(
        { type: "cloudkia:preview-connect", version: 1 },
        frameOriginRef.current || "*",
      );
    };
    window.addEventListener("message", receiveViewport);
    requestViewport();
    // Reconnect after loads and refresh measurements after SPA navigation.
    const timer = window.setInterval(requestViewport, 1000);
    return () => {
      window.removeEventListener("message", receiveViewport);
      window.clearInterval(timer);
    };
  }, [websiteUrl, previewVersion, device]);

  const matchingPage = viewport && websiteUrl && viewport.path === new URL(websiteUrl).pathname;
  const canShowOverlay = !websiteUrl || matchingPage;
  const scrollPreview = useCallback((deltaX, deltaY) => {
    if (!websiteUrl || !viewport || !frameOrigin) return;
    const pageLengths = Math.max(1, (viewport.height - deviceHeight) / deviceHeight);
    const speed = Math.min(5, Math.sqrt(pageLengths));
    frameRef.current?.contentWindow?.postMessage(
      { type: "cloudkia:preview-scroll", version: 1, deltaX, deltaY: Math.max(-2000, Math.min(2000, deltaY * speed)) },
      frameOrigin,
    );
  }, [websiteUrl, viewport, frameOrigin, deviceHeight]);
  const scrollToPreview = y => {
    if (!websiteUrl || !viewport || !frameOrigin) return;
    frameRef.current?.contentWindow?.postMessage(
      { type: "cloudkia:preview-scroll-to", version: 1, y },
      frameOrigin,
    );
  };
  const scrollRange = Math.max(0, (viewport?.height || 0) - deviceHeight);
  const thumbHeight = viewport ? Math.max(28, Math.min(deviceHeight, deviceHeight * deviceHeight / viewport.height)) : deviceHeight;
  const thumbTravel = deviceHeight - thumbHeight;
  const thumbTop = scrollRange && thumbTravel ? Math.min(thumbTravel, viewport.scrollY / scrollRange * thumbTravel) : 0;
  const onScrollbarPointerMove = event => {
    const drag = scrollbarDragRef.current;
    if (!drag || !thumbTravel) return;
    const nextTop = Math.max(0, Math.min(thumbTravel, drag.top + (event.clientY - drag.y) / scale));
    scrollToPreview(nextTop / thumbTravel * scrollRange);
  };
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return undefined;
    const onWheel = event => {
      event.preventDefault();
      event.stopPropagation();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? deviceHeight : 1;
      scrollPreview(event.deltaX * unit, event.deltaY * unit);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [scrollPreview, deviceHeight]);
  const overlayStyle = {
    opacity: mode !== "scroll" ? opacity / 100 : undefined,
    ...(websiteUrl && viewport ? {
      width: viewport.width,
      height: viewport.height,
      right: "auto",
      bottom: "auto",
      transform: `translate(${-viewport.scrollX}px, ${-viewport.scrollY}px)`,
    } : {}),
  };

  const max = Math.max(1, ...report.points.map((point) => point.count));
  return (
    <div className="live-map">
      {mode !== "scroll" && (
        <div className="live-map__filters" role="group" aria-label="Map display filters">
          <label>
            Minimum clicks per spot
            <select value={minimumClicks} onChange={(event) => setMinimumClicks(Number(event.target.value))}>
              <option value={1}>All spots</option>
              <option value={2}>2+ clicks</option>
              <option value={5}>5+ clicks</option>
              <option value={10}>10+ clicks</option>
              <option value={25}>25+ clicks</option>
            </select>
          </label>
          <label>
            Page section
            <select value={section} onChange={(event) => setSection(event.target.value)}>
              <option value="all">Full page</option>
              <option value="top">Top (0–33%)</option>
              <option value="middle">Middle (33–67%)</option>
              <option value="bottom">Bottom (67–100%)</option>
            </select>
          </label>
          <label>
            Overlay opacity: {opacity}%
            <input type="range" min="20" max="100" step="5" value={opacity}
              disabled={!overlay} onChange={(event) => setOpacity(Number(event.target.value))} />
          </label>
          {mode === "heat" && (
            <label>
              Spot size: {spotSize}%
              <input type="range" min="40" max="140" step="10" value={spotSize}
                disabled={!overlay} onChange={(event) => setSpotSize(Number(event.target.value))} />
            </label>
          )}
          <button type="button" onClick={resetFilters}>Reset view</button>
          <p role="status">
            Showing {visiblePoints.length} of {report.points.length} grouped click spots.
            {!overlay && " Overlay is hidden; enable Show overlay to see the spots."}
          </p>
        </div>
      )}
      {mode !== "scroll" && <p className="live-map__description">Total clicks counts individual click events. Nearby clicks are combined into one spot on a 2% position grid; each spot can represent multiple clicks.</p>}
      <p className="live-map__description">
        This preview is scroll-only. Open the tracked page in a new tab to interact with it.
      </p>
      <p className="live-map__description">{deviceWidth} × {deviceHeight} viewport · Displayed at {Math.round(scale * 100)}%</p>
      {websiteUrl && !viewport && <p className="live-map__description" role="status">Waiting for the page to connect. The overlay appears when its position can be synchronized.</p>}
      {websiteUrl && viewport && !matchingPage && <p className="live-map__description" role="status">You opened a different page. <button type="button" onClick={() => setPreviewVersion(value => value + 1)}>Return to tracked page</button></p>}
      {websiteUrl && (
        <p className="live-map__description">
          If the website blocks embedding,{" "}
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
            open the tracked page in a new tab
          </a>.
        </p>
      )}
      <div className="live-map__preview" ref={previewRef}>
        <div className="live-map__viewport" ref={viewportRef} style={{ width: deviceWidth * scale, height: deviceHeight * scale }}
          onTouchStart={event => { touchYRef.current = event.touches[0]?.clientY ?? null; }}
          onTouchMove={event => {
            const nextY = event.touches[0]?.clientY;
            if (touchYRef.current === null || nextY === undefined) return;
            scrollPreview(0, touchYRef.current - nextY);
            touchYRef.current = nextY;
          }}
          onTouchEnd={() => { touchYRef.current = null; }}>
      <div
        className={`live-map__canvas ${websiteUrl ? "" : "live-map__canvas--grid"}`}
        style={{ width: deviceWidth, height: deviceHeight, transform: `scale(${scale})` }}
      >
        {websiteUrl ? (
          <iframe
            ref={frameRef}
            key={`${websiteUrl}:${device}:${previewVersion}`}
            src={websiteUrl}
            title={`Tracked page: ${report.page}`}
            tabIndex={-1}
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="no-referrer"
            onLoad={() => { setViewport(null); frameOriginRef.current = null; setFrameOrigin(null); }}
          />
        ) : (
          <span className="live-map__placeholder">
            {websiteBase
              ? "The selected tracked page does not have a valid website URL."
              : "Set the Website URL in Chatbot Settings to enable the preview."}
          </span>
        )}
        {overlay && canShowOverlay && (
          <div
            className={`report-overlay ${mode === "scroll" ? "report-overlay--scroll" : ""}`}
            style={overlayStyle}
          >
            {mode === "scroll"
              ? report.reach.map((row) => (
                  <div
                    className="report-scroll-band"
                    key={row.depth}
                    style={{
                      top: `${row.depth}%`,
                      height: "auto",
                      transform: row.depth === 100 ? "translateY(-100%)" : undefined,
                    }}
                  >
                    <span>
                      {row.percent}% reached {row.depth}% of the page
                    </span>
                  </div>
                ))
              : visiblePoints.map((point) => (
                  <span
                    key={`${point.x}:${point.y}`}
                    className={
                      mode === "heat"
                        ? "report-heat-point"
                        : "report-click-point"
                    }
                    title={`${point.count} clicks`}
                    style={{
                      left: `${point.x}%`,
                      top: `${point.y}%`,
                      width:
                        mode === "heat"
                          ? (30 + (70 * point.count) / max) * spotSize / 100
                          : undefined,
                      height:
                        mode === "heat"
                          ? (30 + (70 * point.count) / max) * spotSize / 100
                          : undefined,
                    }}
                  >
                    {mode === "click" ? point.count : null}
                  </span>
                ))}
          </div>
        )}
        {websiteUrl && viewport && scrollRange > 0 && (
          <div className="live-map__scrollbar" style={{ height: deviceHeight }}
            onPointerDown={event => {
              const thumb = event.target.closest(".live-map__scrollbar-thumb");
              const top = thumb ? thumbTop : Math.max(0, Math.min(thumbTravel, (event.clientY - event.currentTarget.getBoundingClientRect().top) / scale - thumbHeight / 2));
              if (!thumb) scrollToPreview(top / thumbTravel * scrollRange);
              scrollbarDragRef.current = { y: event.clientY, top };
              event.currentTarget.setPointerCapture(event.pointerId);
              event.preventDefault();
            }}
            onPointerMove={onScrollbarPointerMove}
            onPointerUp={event => { scrollbarDragRef.current = null; event.currentTarget.releasePointerCapture(event.pointerId); }}
            onPointerCancel={() => { scrollbarDragRef.current = null; }}>
            <div className="live-map__scrollbar-thumb" style={{ height: thumbHeight, top: thumbTop }} />
          </div>
        )}
      </div>
        </div>
      </div>
      {mode !== "scroll" && !report.points.length && (
        <p>No clicks recorded for this selection.</p>
      )}
      {mode !== "scroll" && report.points.length > 0 && !visiblePoints.length && (
        <p>No spots match these filters. Lower the minimum clicks or choose another page section.</p>
      )}
      {report.pointLimitReached && (
        <p>Showing the 500 most active positions.</p>
      )}
    </div>
  );
}
